import React from 'react';

import PropTypes from 'prop-types';
import cn from 'classnames';
import { IoClose } from 'react-icons/io5';
import { useFormikContext } from 'formik';

import { Text, Badge } from 'modules/common';

function FormTags({
  name,
  label,
  trailingItem,
  error,
  leadingItem,
  onChange,
  required,
  labelClassName,
  onSetFieldValue,
  containerClassName,
  errorClassName,
  isOptional,
  helperText,
  disabled,
  value: $value,
  isUnique,
  withHiddenLabel,
  ...rest
}) {
  const [keyword, setKeyword] = React.useState();
  const [myError, setMyError] = React.useState();
  const { errors, setErrors } = useFormikContext() || {};
  const value = !Array.isArray($value) ? [] : $value;
  const ref = React.useRef();

  const itemProps = {
    buttonProps: {
      disabled,
      size: 'xs',
    },
    iconProps: {
      className: 'h-4 w-4',
    },
  };

  const handleOnFocus = () => {
    try {
      ref.current.focus();
    } catch (err) {
      // do_nothing
    }
  };

  const handleValidation = (func) => (v) => {
    const errorMessage = 'Field should be unique.';

    if (value?.includes(v) && isUnique) {
      if (typeof setErrors === 'function') {
        setErrors({ ...errors, [name]: errorMessage });

        if (typeof error === 'boolean' && !error) {
          setMyError(errorMessage);
        }

        setKeyword('');
        return;
      }

      setMyError(errorMessage);
      setKeyword('');
      return;
    }

    setMyError('');

    if (typeof setErrors === 'function') {
      setErrors({ ...errors, [name]: undefined });
    }

    func(v);
  };

  const handleOnAdd = handleValidation((v) => {
    setKeyword('');

    if (typeof onSetFieldValue === 'function') {
      onSetFieldValue(name, [...(value || []), v]);
      return;
    }

    if (typeof onChange === 'function') {
      onChange((prev) => ({
        ...prev,
        [name]: [...(value || []), v],
      }));
    }
  });

  const handleOnKeydown = (e) => {
    if (e?.keyCode !== 13) return;

    const v = e?.target?.value;
    handleOnAdd(v);
  };

  const handleOnRemove = (index) => {
    const v = value?.filter((item, key) => key !== index);

    if (typeof onSetFieldValue === 'function') {
      onSetFieldValue(name, v);
      return;
    }

    if (typeof onChange === 'function') {
      onChange((prev) => ({
        ...prev,
        [name]: v,
      }));
    }
  };

  return (
    <div className="w-full">
      <div className="w-full relative">
        <div
          className={cn(
            'form-container transition duration-300 ease-in-out',
            containerClassName,
            {
              'form-container': !error,
              'form-container-error': error,
            }
          )}
          onClick={handleOnFocus}
          role="presentation"
        >
          {withHiddenLabel ? (
            <div className="ds-form-label opacity-0">RO</div>
          ) : (
            <>
              {label && (
                <label
                  className={cn(
                    'ds-form-label flex items-center',
                    labelClassName
                  )}
                  htmlFor={rest?.id ?? name}
                >
                  <div className="flex-1">
                    {label}{' '}
                    {required ? (
                      <span className="text-utility-danger-base">*</span>
                    ) : (
                      ''
                    )}
                  </div>

                  {isOptional && <div className="font-normal">Optional</div>}
                </label>
              )}
            </>
          )}

          <div
            className={cn(
              'transition duration-300 ease-in-out relative gap-[10px]',
              {
                'ds-form-input': !error,
                'ds-form-input-error': error,
                'ds-form-input-disabled': disabled,
                'p-[9px]': value?.length > 0,
              }
            )}
          >
            {Boolean(leadingItem) && (
              <div
                className={cn(
                  'transition duration-300 ease-in-out font-light flex flex-col justify-center',
                  {
                    'form-icon': !disabled,
                    'form-icon-disabled': disabled,
                  }
                )}
              >
                {typeof leadingItem === 'function'
                  ? leadingItem(itemProps)
                  : leadingItem}
              </div>
            )}

            <div className="flex-1 relative">
              <div className="flex items-center flex-wrap gap-ds-8 flex-1">
                {value?.map((item, key) => (
                  <Badge
                    type="secondary"
                    label={item}
                    onClose={() => handleOnRemove(key)}
                    disabled={disabled}
                  />
                ))}

                <input
                  id={rest?.id ?? name}
                  type="text"
                  autoComplete="off"
                  className={cn(
                    'flex-1 w-10/12 min-w-[50px] transition-all duration-300 ease-in-out',
                    'disabled:cursor-not-allowed',
                    { 'bg-utility-danger-muted': error },
                    !disabled
                      ? 'placeholder-placeholder'
                      : 'placeholder-disabled'
                  )}
                  name={name}
                  onKeyDown={handleOnKeydown}
                  onChange={(e) => setKeyword(e.target.value)}
                  value={keyword}
                  required={required}
                  disabled={disabled}
                  {...rest}
                  style={{ outline: 'none', ...rest?.style }}
                  ref={ref}
                />
              </div>

              {keyword?.length > 0 && (
                <div className="absolute origin-top w-full mt-1 z-10">
                  <div
                    className="bg-primitive-white hover:bg-light p-ds-12 py-[10px] shadow-md rounded flex items-center cursor-pointer"
                    role="presentation"
                    onClick={() => handleOnAdd(keyword)}
                  >
                    <div className="flex-1 text-body text-muted">
                      Add{' '}
                      <span className="font-semibold">
                        &quot;{keyword}&quot;
                      </span>
                    </div>

                    <IoClose
                      className="h-4 w-4 cursor-pointer text-muted"
                      onClick={(e) => {
                        e.stopPropagation();
                        setKeyword('');
                      }}
                    />
                  </div>
                </div>
              )}
            </div>

            {Boolean(trailingItem) && (
              <div
                className={cn(
                  'transition duration-300 ease-in-out font-light flex flex-col justify-center',
                  {
                    'form-icon': !disabled,
                    'form-icon-disabled': disabled,
                  }
                )}
              >
                {typeof trailingItem === 'function'
                  ? trailingItem(itemProps)
                  : trailingItem}
              </div>
            )}
          </div>
        </div>
      </div>

      {(Boolean(error) || Boolean(helperText) || Boolean(myError)) && (
        <Text
          type="caption"
          className={cn(
            'mt-ds-6',
            {
              'ds-form-helper': !error,
              'ds-form-helper-error': error,
              'ds-form-helper-disabled': disabled,
            },
            errorClassName
          )}
        >
          <span>{myError || error || helperText || ''}</span>
          <span className="form-error-badge" />
        </Text>
      )}
    </div>
  );
}

FormTags.defaultProps = {
  label: 'Name',
  containerClassName: '',
  labelClassName: '',
  errorClassName: '',
  required: false,
  error: false,
  trailingItem: false,
  onSetFieldValue: false,
  onChange: false,
  leadingItem: false,
  isOptional: false,
  helperText: '',
  disabled: false,
  isUnique: false,
  withHiddenLabel: false,
  value: [],
};

FormTags.propTypes = {
  label: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element,
    PropTypes.bool,
  ]),
  value: PropTypes.instanceOf(Array),
  isOptional: PropTypes.bool,
  withHiddenLabel: PropTypes.bool,
  helperText: PropTypes.string,
  disabled: PropTypes.bool,
  isUnique: PropTypes.bool,
  name: PropTypes.string.isRequired,
  errorClassName: PropTypes.string,
  containerClassName: PropTypes.string,
  labelClassName: PropTypes.string,
  required: PropTypes.bool,
  trailingItem: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.bool,
    PropTypes.string,
    PropTypes.instanceOf(Object),
    PropTypes.instanceOf(Function),
  ]),
  error: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element,
    PropTypes.instanceOf(Object),
    PropTypes.bool,
  ]),
  onSetFieldValue: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.instanceOf(Object),
  ]),
  leadingItem: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.element,
    PropTypes.string,
    PropTypes.instanceOf(Object),
    PropTypes.instanceOf(Function),
  ]),
  onChange: PropTypes.oneOfType([PropTypes.bool, PropTypes.instanceOf(Object)]),
};

export default React.memo(FormTags);
